// Lots imprimeur — Phase 2 (2026-06-10).
//
// Every "Envoyer à l'imprimeur" auto-drafts a batch (worker hook): per order,
// sheets = ceil(pages/4) × albums and covers = albums, priced at the settings
// rate card. Here the admin reviews/edits the unit prices per batch (the bulk
// discount case: 3 covers → 35 MAD, 20 covers → 25 MAD), tracks the "à payer"
// counter, and settles one or many batches at once (per-batch today, weekly
// later — same button).

import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { printApi, type PrintBatch } from '@/lib/api';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/Button';
import { useT } from '@/lib/i18n';
import { useIsPhone } from '@/lib/phoneViewport';
import { PhoneActionBar, SelectTick } from '@/components/admin/PhoneActionBar';

const GOLD_DARK = 'var(--color-adm-gold-ink)'; // #8B6F26 in light
// Payment pills: the light colours of today, the status tokens in night mode.
const PAID_PILL = 'bg-[rgba(16,185,129,.1)] text-[#047857] dark:bg-status-done-bg dark:text-status-done';
const DUE_PILL = 'bg-[rgba(217,119,6,.1)] text-[#B45309] dark:bg-status-waiting-bg dark:text-status-waiting';
const fmtMad = (n: number) => `${(Math.round(n * 100) / 100).toLocaleString('fr-FR')} MAD`;
const fmtDate = (s: string) => new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

// ─── Batch detail / price edit modal ─────────────────────────────────────────
function BatchModal({ batch, onClose }: { batch: PrintBatch; onClose: () => void }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [sheetPrice, setSheetPrice] = useState(String(batch.sheet_unit_price));
  const [coverPrice, setCoverPrice] = useState(String(batch.cover_unit_price));

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const lines = batch.print_batch_orders ?? [];
  const sheets = lines.reduce((s, l) => s + l.sheets, 0);
  const covers = lines.reduce((s, l) => s + l.covers, 0);
  const liveTotal = sheets * (Number(sheetPrice) || 0) + covers * (Number(coverPrice) || 0);
  const dirty = Number(sheetPrice) !== Number(batch.sheet_unit_price) || Number(coverPrice) !== Number(batch.cover_unit_price);

  const save = useMutation({
    mutationFn: () => printApi.updateBatch(batch.id, {
      sheet_unit_price: Number(sheetPrice),
      cover_unit_price: Number(coverPrice),
    }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['print-batches'] }); onClose(); },
  });

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: 'rgba(17,17,17,0.10)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
      onClick={onClose}
    >
      <div className="w-full max-w-[560px] max-h-[88vh] overflow-y-auto bg-white rounded-2xl p-5 shadow-2xl adm-rise" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2.5 mb-1">
          <h3 className="text-[16px] font-semibold" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
            {t('Lot du {date}', { date: fmtDate(batch.sent_at) })}
          </h3>
          <span
            className={`px-2 py-0.5 rounded-full text-[10.5px] font-bold ${batch.payment_status === 'payé' ? PAID_PILL : DUE_PILL}`}
          >
            {batch.payment_status === 'payé' ? t('Payé ✓') : t('À payer')}
          </span>
          <button type="button" onClick={onClose} aria-label={t('Fermer')} className="ml-auto w-11 h-11 md:w-8 md:h-8 shrink-0 rounded-lg border border-gray-200 text-gray-400 hover:text-gray-700 flex items-center justify-center">✕</button>
        </div>
        <p className="text-xs text-gray-400 mb-4">
          {(lines.length > 1 ? t('{n} commandes', { n: lines.length }) : t('{n} commande', { n: lines.length }))}
          {' · '}{t('{n} feuilles A3', { n: sheets })}
          {' · '}{(covers > 1 ? t('{n} couvertures', { n: covers }) : t('{n} couverture', { n: covers }))}
        </p>

        {/* Unit prices — the dynamic part */}
        <div className="grid grid-cols-2 gap-3 rounded-xl p-3 mb-4" style={{ background: 'rgba(201,162,74,.06)', border: '1px solid rgba(201,162,74,.25)' }}>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{t('Prix / feuille A3 (MAD)')}</label>
            <input
              className="w-full px-3 py-2 min-h-11 md:min-h-0 border border-gray-200 rounded-lg text-base md:text-sm bg-white"
              type="number" min="0" step="0.5" value={sheetPrice} onChange={(e) => setSheetPrice(e.target.value)}
              disabled={batch.payment_status === 'payé'}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{t('Prix / couverture (MAD)')}</label>
            <input
              className="w-full px-3 py-2 min-h-11 md:min-h-0 border border-gray-200 rounded-lg text-base md:text-sm bg-white"
              type="number" min="0" step="0.5" value={coverPrice} onChange={(e) => setCoverPrice(e.target.value)}
              disabled={batch.payment_status === 'payé'}
            />
          </div>
          <p className="col-span-2 text-[11px] text-gray-400">
            {t('Gros lot = prix plus bas — modifiez ici, les coûts par commande se recalculent automatiquement.')}
          </p>
        </div>

        {/* Lines */}
        <div className="space-y-1.5 mb-4">
          {lines.map((l) => (
            <div key={l.id} className="flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-1 text-xs py-2 px-3 rounded-lg border border-gray-100">
              <Link to={`/admin/orders/${l.shopify_order_id}`} className="font-bold" style={{ color: GOLD_DARK }}>
                #{l.shopify_order_id.slice(-4)}
              </Link>
              <span className="text-gray-500">{(l.albums > 1 ? t('{n} albums', { n: l.albums }) : t('{n} album', { n: l.albums }))} · {t('{n} p.', { n: l.pages })}</span>
              <span className="text-gray-400">{t('{n} feuilles + {c} couv.', { n: l.sheets, c: l.covers })}</span>
              <span className="ml-auto font-bold tabular-nums text-gray-700">
                {fmtMad(l.sheets * (Number(sheetPrice) || 0) + l.covers * (Number(coverPrice) || 0))}
              </span>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-bold text-gray-800">{t('Total')} : <span style={{ color: GOLD_DARK }}>{fmtMad(liveTotal)}</span></p>
          {batch.payment_status !== 'payé' && (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={onClose}>{t('Fermer')}</Button>
              <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!dirty}>{t('Enregistrer les prix')}</Button>
            </div>
          )}
        </div>
        {save.isError && <p className="text-xs text-red-600 mt-2">{(save.error as Error).message}</p>}
      </div>
    </div>,
    document.body,
  );
}

// ─── Phone card ───────────────────────────────────────────────────────────────
// The table row, stacked: when + which orders on top, what it holds below,
// the amount and payment state on the right. Tap = the same modal as a row.
function BatchCard({
  batch: b, selected, onToggle, onOpen,
}: { batch: PrintBatch; selected: boolean; onToggle: () => void; onOpen: () => void }) {
  const { t } = useT();
  const lines = b.print_batch_orders ?? [];
  const sheets = lines.reduce((s, l) => s + l.sheets, 0);
  const covers = lines.reduce((s, l) => s + l.covers, 0);
  const paid = b.payment_status === 'payé';
  return (
    <li
      data-batch-card
      onClick={onOpen}
      className={`flex items-start gap-1 pl-1.5 pr-4 py-2.5 cursor-pointer transition-colors ${selected ? 'bg-brand-50' : 'active:bg-[#FBF9F3] dark:active:bg-adm-surface-2'}`}
    >
      {paid ? <span className="w-11 shrink-0" aria-hidden /> : (
        <SelectTick
          checked={selected}
          onChange={onToggle}
          label={t('Sélectionner le lot du {date}', { date: fmtDate(b.sent_at) })}
        />
      )}
      <div className="min-w-0 flex-1 pt-2">
        <div className="flex items-start justify-between gap-2">
          <p className="font-semibold text-gray-800 truncate">{fmtDate(b.sent_at)}</p>
          <span className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-bold ${paid ? PAID_PILL : DUE_PILL}`}>
            {paid ? t('Payé ✓') : t('À payer')}
          </span>
        </div>
        <p className="text-[12px] text-gray-400 truncate">{lines.map((l) => `#${l.shopify_order_id.slice(-4)}`).join(' · ')}</p>
        <div className="mt-1.5 flex items-end justify-between gap-2">
          <p className="text-xs text-gray-500">
            {t('{c} cmd · {s} feuilles · {v} couv.', { c: lines.length, s: sheets, v: covers })}
          </p>
          <p className="shrink-0 font-bold tabular-nums" style={{ color: GOLD_DARK }}>{fmtMad(Number(b.total_amount))}</p>
        </div>
      </div>
    </li>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function AdminImpressionPage() {
  const { t } = useT();
  useEffect(() => { document.title = 'Impression — Admin Storiz'; }, []);
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ['print-batches'], queryFn: () => printApi.batches() });
  const batches = data?.batches ?? [];
  const unpaid = data?.unpaid_total ?? 0;

  const phone = useIsPhone();
  const [openBatch, setOpenBatch] = useState<PrintBatch | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const unpaidBatches = useMemo(() => batches.filter((b) => b.payment_status === 'à_payer'), [batches]);
  const selectedTotal = unpaidBatches.filter((b) => selected.has(b.id)).reduce((s, b) => s + Number(b.total_amount), 0);

  const pay = useMutation({
    mutationFn: (ids: string[]) => printApi.pay(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print-batches'] });
      setSelected(new Set());
    },
  });

  const toggle = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <AdminLayout
      title={t('Impression')}
      subtitle={t('Lots envoyés à l\'imprimeur — quantités, prix, paiements')}
      actions={
        // Phone: the same action lives in the pinned bar at the bottom instead.
        selected.size > 0 && !phone ? (
          <Button size="sm" onClick={() => pay.mutate(Array.from(selected))} loading={pay.isPending}>
            {t('Marquer payé ({n}) · {total}', { n: selected.size, total: fmtMad(selectedTotal) })}
          </Button>
        ) : undefined
      }
    >
      <div className="max-w-6xl space-y-5">

        {/* À payer counter */}
        <div className="adm-rise flex flex-wrap items-center gap-4 bg-white rounded-xl border border-gray-200 px-5 py-4">
          <div>
            <p className="text-[11px] uppercase tracking-wide font-semibold text-gray-400">{t('À payer à l\'imprimeur')}</p>
            <p className={`text-2xl font-bold tabular-nums ${unpaid > 0 ? 'text-status-waiting' : 'text-[#047857] dark:text-status-done'}`} style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
              {fmtMad(unpaid)}
            </p>
          </div>
          <div className="text-xs text-gray-400">
            {(unpaidBatches.length > 1 ? t('{n} lots en attente · cochez puis « Marquer payé » — un lot ou plusieurs d\'un coup.', { n: unpaidBatches.length }) : t('{n} lot en attente · cochez puis « Marquer payé » — un lot ou plusieurs d\'un coup.', { n: unpaidBatches.length }))}
          </div>
        </div>

        {/* Batches */}
        <div className="adm-rise bg-white rounded-xl border border-gray-200 overflow-hidden" style={{ animationDelay: '50ms' }}>
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-800">🖨 {t('Lots d\'impression')}</h2>
            <p className="text-[11px] text-gray-400 mt-0.5">{t('Un lot est créé automatiquement à chaque « Envoyer à l\'imprimeur ». Cliquez un lot pour ajuster les prix.')}</p>
          </div>
          {phone ? (
            <ul className="divide-y divide-gray-100">
              {batches.map((b) => (
                <BatchCard
                  key={b.id}
                  batch={b}
                  selected={selected.has(b.id)}
                  onToggle={() => toggle(b.id)}
                  onOpen={() => setOpenBatch(b)}
                />
              ))}
              {batches.length === 0 && (
                <li className="px-5 py-10 text-center text-sm text-gray-400">
                  {t('Aucun lot — le premier sera créé automatiquement au prochain « Envoyer à l\'imprimeur » (page Commandes).')}
                </li>
              )}
            </ul>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                  <th className="pl-5 pr-2 py-2.5 w-8" />
                  <th className="px-2 py-2.5 font-semibold">{t('Lot')}</th>
                  <th className="px-3 py-2.5 font-semibold">{t('Contenu')}</th>
                  <th className="px-3 py-2.5 font-semibold hidden md:table-cell">{t('Prix unitaires')}</th>
                  <th className="px-3 py-2.5 font-semibold text-right">{t('Total')}</th>
                  <th className="px-5 py-2.5 font-semibold text-right">{t('Paiement')}</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => {
                  const lines = b.print_batch_orders ?? [];
                  const sheets = lines.reduce((s, l) => s + l.sheets, 0);
                  const covers = lines.reduce((s, l) => s + l.covers, 0);
                  return (
                    <tr key={b.id} className="border-b border-gray-50 hover:bg-[#FBF9F3] dark:hover:bg-adm-surface-2 transition-colors cursor-pointer" onClick={() => setOpenBatch(b)}>
                      <td className="pl-5 pr-2 py-3" onClick={(e) => e.stopPropagation()}>
                        {b.payment_status === 'à_payer' && (
                          <input type="checkbox" checked={selected.has(b.id)} onChange={() => toggle(b.id)} className="accent-[#C9A24A] w-4 h-4 cursor-pointer" />
                        )}
                      </td>
                      <td className="px-2 py-3">
                        <p className="font-semibold text-gray-800">{fmtDate(b.sent_at)}</p>
                        <p className="text-[11px] text-gray-400">{lines.map((l) => `#${l.shopify_order_id.slice(-4)}`).join(' · ')}</p>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-500">
                        {t('{c} cmd · {s} feuilles · {v} couv.', { c: lines.length, s: sheets, v: covers })}
                      </td>
                      <td className="px-3 py-3 hidden md:table-cell text-xs text-gray-500 tabular-nums">
                        {t('{s} / feuille · {v} / couv.', { s: Number(b.sheet_unit_price), v: Number(b.cover_unit_price) })}
                      </td>
                      <td className="px-3 py-3 text-right font-bold tabular-nums" style={{ color: GOLD_DARK }}>{fmtMad(Number(b.total_amount))}</td>
                      <td className="px-5 py-3 text-right">
                        <span
                          className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${b.payment_status === 'payé' ? PAID_PILL : DUE_PILL}`}
                        >
                          {b.payment_status === 'payé' ? 'Payé ✓' : 'À payer'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {batches.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-gray-400">
                    {t('Aucun lot — le premier sera créé automatiquement au prochain « Envoyer à l\'imprimeur » (page Commandes).')}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          )}
        </div>
      </div>

      {selected.size > 0 && (
        <PhoneActionBar
          label={t('{n} lot(s) · {total}', { n: selected.size, total: fmtMad(selectedTotal) })}
          onClear={() => setSelected(new Set())}
          clearLabel={t('Effacer')}
        >
          <Button onClick={() => pay.mutate(Array.from(selected))} loading={pay.isPending}>
            {t('Marquer payé ({n})', { n: selected.size })}
          </Button>
        </PhoneActionBar>
      )}

      {openBatch && <BatchModal batch={openBatch} onClose={() => setOpenBatch(null)} />}
    </AdminLayout>
  );
}
